import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container,
    Paper,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    Tabs,
    Tab,
    CircularProgress,
    InputAdornment,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    MenuItem,
    FormControlLabel,
    Checkbox
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { registerWithEmail, loginWithEmail, resetPassword } from "../firebase/auth";
import { setDocument } from "../firebase/firestore";
import UniversityLogo from "./Nims_Logo.png";

const COLLEGES = [
    "National Institute of Medical Sciences & Research",
    "Nims Institute of Engineering & Technology",
    "Nims School of Business",
    "Nims School of Law",
    "Nims Institute of Hotel Management",
    "Nims Dental College & Hospital",
    "Nims Institute of Pharmacy",
    "Nims School of Nursing",
    "Nims College of Physiotherapy & Occupational Therapy",
    "Nims College of Allied & Health Care Sciences (Paramedical)",
    "Nims Institute of Humanities & Liberal Arts",
    "Nims Institute of Public Health and Governance",
    "Nims Institute of Fashion Technology",
    "Nims Institute of Aviation",
    "Nims School of Forensic Sciences",
    "Marik Institute of Computing, AI, Robotics & Cybernetics",
    "Nims Institute of Travel & Tourism Management"
];

const loginSchema = yup.object({
    email: yup.string().email("Invalid email").required("Email is required"),
    password: yup.string().min(6, "Minimum 6 characters").required("Password is required")
});

const registerSchema = yup.object().shape({
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    phone: yup
        .string()
        .matches(/^[0-9]{10}$/, "Phone must be exactly 10 digits")
        .required("Phone number is required"),
    companyName: yup.string().required("Company name is required"),
    isNimsStudent: yup.boolean(),
    collegeName: yup.string().test(
        "college-required",
        "College is required for NIMS students",
        function (value) {
            const { isNimsStudent } = this.parent;
            if (isNimsStudent && !value) {
                return false;
            }
            return true;
        }
    ),
    password: yup
        .string()
        .required("Password is required")
        .min(8, "Minimum 8 characters")
        .matches(/[A-Z]/, "At least one uppercase letter")
        .matches(/[a-z]/, "At least one lowercase letter")
        .matches(/[0-9]/, "At least one number")
        .matches(/[^A-Za-z0-9]/, "At least one special character"),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref("password")], "Passwords must match")
        .required("Confirm password is required")
});

function TabPanel({ children, value, index }) {
    return value === index ? children : null;
}

export default function LoginPage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [openReset, setOpenReset] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetSuccess, setResetSuccess] = useState("");

    const loginForm = useForm({ resolver: yupResolver(loginSchema) });

    const registerForm = useForm({
        resolver: yupResolver(registerSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            companyName: "",
            isNimsStudent: false,
            collegeName: "",
            password: "",
            confirmPassword: ""
        }
    });

    const isNimsStudent = registerForm.watch("isNimsStudent");

    const handleLogin = useCallback(
        async (data) => {
            try {
                setLoading(true);
                setError("");
                console.log("Attempting login with:", data.email);
                await loginWithEmail(data.email, data.password);
                console.log("Login successful");
                navigate("/");
            } catch (err) {
                console.error("Login failed:", err);
                setError(err.message || "Login failed. Please check your credentials.");
            } finally {
                setLoading(false);
            }
        },
        [navigate]
    );

    const handleRegister = useCallback(
        async (data) => {
            try {
                setLoading(true);
                setError("");
                console.log("Starting registration for:", data.email);

                const user = await registerWithEmail(data.email, data.password);
                console.log("User created in Firebase Auth:", user.uid);

                const userData = {
                    uid: user.uid,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    email: data.email,
                    phone: data.phone,
                    companyName: data.companyName,
                    isNimsStudent: data.isNimsStudent,
                    collegeName: data.isNimsStudent ? data.collegeName : "",
                    role: "student",
                    status: "active",
                    emailVerified: false,
                    profileComplete: false,
                    createdAt: new Date(),
                    updatedAt: new Date()
                };

                console.log("Saving user data to Firestore:", userData);
                await setDocument("users", user.uid, userData, false);
                console.log("User data saved successfully to Firestore");

                navigate("/");
            } catch (err) {
                console.error("Registration failed:", err);
                setError(err.message || "Registration failed. Please try again.");
            } finally {
                setLoading(false);
            }
        },
        [navigate]
    );

    const handleResetPassword = async () => {
        try {
            setError("");
            await resetPassword(resetEmail);
            setResetSuccess("Password reset email sent successfully");
            setOpenReset(false);
            setResetEmail("");
        } catch (err) {
            console.error("Password reset failed:", err);
            setError(err.message || "Failed to send reset email");
        }
    };

    return (
        <Container maxWidth="sm" sx={{ minHeight: "100vh", display: "flex", alignItems: "center", py: 4 }}>
            <Paper elevation={3} sx={{ width: "100%", p: 4, borderRadius: 3 }}>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Box
                        component="img"
                        src={UniversityLogo}
                        alt="NIMS Logo"
                        sx={{ height: 64, mb: 2, objectFit: "contain" }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5, fontSize: "0.95rem" }}>
                        NIMS SUSHMA INNOVATION AND INCUBATION CENTER (NSIIC)
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {resetSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResetSuccess("")}>
                        {resetSuccess}
                    </Alert>
                )}

                <Tabs
                    value={tab}
                    onChange={(_, v) => {
                        setTab(v);
                        setError("");
                        setResetSuccess("");
                    }}
                    sx={{ mb: 2 }}
                    variant="fullWidth"
                >
                    <Tab label="Login" />
                    <Tab label="Register" />
                </Tabs>

                <TabPanel value={tab} index={0}>
                    <Box
                        component="form"
                        onSubmit={loginForm.handleSubmit(handleLogin)}
                        noValidate
                        sx={{ mt: 1 }}
                    >
                        <Controller
                            name="email"
                            control={loginForm.control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    autoComplete="email"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                        <Controller
                            name="password"
                            control={loginForm.control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    label="Password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 1 }}>
                            <Button
                                onClick={() => setOpenReset(true)}
                                sx={{ textTransform: "none" }}
                                size="small"
                            >
                                Forgot password?
                            </Button>
                        </Box>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 2 }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Sign In"}
                        </Button>
                    </Box>
                </TabPanel>

                <TabPanel value={tab} index={1}>
                    <Box
                        component="form"
                        onSubmit={registerForm.handleSubmit(handleRegister)}
                        noValidate
                        sx={{ mt: 1 }}
                    >
                        {["firstName", "lastName", "email", "companyName"].map((name) => (
                            <Controller
                                key={name}
                                name={name}
                                control={registerForm.control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        label={
                                            name === "firstName"
                                                ? "First Name"
                                                : name === "lastName"
                                                    ? "Last Name"
                                                    : name === "email"
                                                        ? "Email"
                                                        : "Company Name"
                                        }
                                        type={name === "email" ? "email" : "text"}
                                        autoComplete={name === "email" ? "email" : "off"}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        ))}

                        <Controller
                            name="phone"
                            control={registerForm.control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    label="Phone Number"
                                    type="tel"
                                    autoComplete="tel"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    inputProps={{ maxLength: 10 }}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                                        field.onChange(value);
                                    }}
                                />
                            )}
                        />

                        <Controller
                            name="isNimsStudent"
                            control={registerForm.control}
                            render={({ field }) => (
                                <FormControlLabel
                                    {...field}
                                    control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                                    label="I am a NIMS University student"
                                    sx={{ my: 1 }}
                                />
                            )}
                        />

                        {isNimsStudent && (
                            <Controller
                                name="collegeName"
                                control={registerForm.control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        select
                                        margin="normal"
                                        fullWidth
                                        label="College Name"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    >
                                        <MenuItem value="">Select a college</MenuItem>
                                        {COLLEGES.map((college) => (
                                            <MenuItem key={college} value={college}>
                                                {college}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        )}

                        {["password", "confirmPassword"].map((name) => (
                            <Controller
                                key={name}
                                name={name}
                                control={registerForm.control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        label={name === "password" ? "Password" : "Confirm Password"}
                                        type={showPassword ? "text" : "password"}
                                        autoComplete={name === "password" ? "new-password" : "new-password"}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                )}
                            />
                        ))}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 2 }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Create Account"}
                        </Button>
                    </Box>
                </TabPanel>

                <Dialog open={openReset} onClose={() => setOpenReset(false)} fullWidth maxWidth="xs">
                    <DialogTitle>Reset Password</DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Email"
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            autoComplete="email"
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                            <Button onClick={() => setOpenReset(false)}>Cancel</Button>
                            <Button
                                onClick={handleResetPassword}
                                variant="contained"
                                disabled={!resetEmail}
                            >
                                Send Reset Email
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            </Paper>
        </Container>
    );
}
